// pages/ExpensePurchaseOrder/EposShipping.js
//
// "Shipping" — EPOs awaiting receipt/shipment.
// Legacy form: Projects/epoShipping.vb => epoDB.GetShippingEPO()
//
// R41 — collapsed the R36 two-tab layout (EPOs Pending Arrival + Search)
// into a single paginated table with the column-filter row UX. The Search
// tab is gone because column filters do its job better (instant filter as
// you type vs click-Search-then-wait).
//
// Backend: GET /api/epo-query/shipping?page=&pageSize=
//   -> PagedResponse<ShippingListRow>
// (No backend change. The R18 server-side search params still work if
// called via URL, just not driven from this page.)
//
// Access: gate prop requireModule="epo.receive".

import React, { useEffect, useState, useCallback, useMemo } from "react";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import { exportAllPagesToCsv, fetchAllPages } from "../../Components/Common/DashboardKit/exportCsv";
import {
    Container, Card, CardBody, Input, Badge, Alert,
    Pagination, PaginationItem, PaginationLink,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
    DashboardPageHeader,
    EmptyState,
    TableSkeleton,
    ColumnFilterRow,
    applyColumnFilters,
    // R44/R46 — applyColumnFilters dropped (server filters now);
    //         useDebouncedValue dropped (text inputs now commit on Enter/blur).
} from "../../Components/Common/DashboardKit";
import api from "../../api";
import useEpoReviewModal from "../../hooks/useEpoReviewModal";

const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
};
const pick = (row, ...keys) => {
    for (const k of keys) {
        if (row != null && row[k] !== undefined && row[k] !== null) return row[k];
    }
    return undefined;
};
const fmtAddress = (r) => {
    const parts = [
        pick(r, "Street1", "street1"),
        pick(r, "City", "city"),
        pick(r, "State", "state"),
        pick(r, "Zip", "zip"),
    ].filter((x) => x != null && String(x).trim() !== "");
    return parts.length ? parts.join(", ") : "—";
};

const EposShipping = () => {
    const navigate = useNavigate();
    const epoReview = useEpoReviewModal({ permissions: { canReceive: true } });

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // R41 — per-column filters.
    const [columnFilters, setColumnFilters] = useState({});
    // R46 — columnFilters dropped; columnFilters is itself the
    //         committed value (ColumnFilterRow defers text updates until
    //         Enter or focus-out).

    // Pagination.
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Round 31 — "all rows" mode: first sort click loads the COMPLETE
    // dataset (same page-walking as Export all). Sorting, filters, and
    // paging then operate on ALL rows, instantly and client-side.
    const [allRows, setAllRows] = useState(null);
    const [loadingAll, setLoadingAll] = useState(false);
    const loadAllRows = async () => {
        if (loadingAll || allRows) return;
        setLoadingAll("Loading all…");
        try {
            const fetched = await fetchAllPages({
                onProgress: (n, t) => setLoadingAll(`Loading all ${n} / ${t}…`),
                fetchPage: async (pageNum, sizeNum) => {
                    const params = { page: pageNum, pageSize: sizeNum };
                    const { data } = await api.get("epo-query/shipping", { params });
                    const list = Array.isArray(data?.Items) ? data.Items
                               : Array.isArray(data?.items) ? data.items
                               : Array.isArray(data?.results) ? data.results
                               : Array.isArray(data?.Results) ? data.Results : [];
                    return { rows: list, total: data?.Total ?? data?.total ?? 0 };
                },
            });
            if (Array.isArray(fetched) && fetched.length === 0 && total > 0) {
                setLoadingAll(false);
                return;
            }
            setAllRows(fetched);
            setPage(1);
        } catch (e) { /* keep server mode on failure */ }
        setLoadingAll(false);
    };
    // Round 29 — full CSV export: same endpoint + filters as the table,
    // looped across all pages (the visible page is just a window).
    const [exporting, setExporting] = useState(false);
    const handleExportAll = async () => {
        try {
            setExporting("Preparing…");
            await exportAllPagesToCsv({
                filename: "epos-shipping-all.csv",
                columns: columns.filter((c) => c.type !== "none"),
                total,
                onProgress: (n, t) => setExporting(`Exporting ${n} / ${t}…`),
                fetchPage: async (pageNum, sizeNum) => {
                    const params = { page: pageNum, pageSize: sizeNum };
                    for (const [k, v] of Object.entries(columnFilters || {})) {
                        if (v == null || v === "") continue;
                        params[`filter${k[0].toUpperCase()}${k.slice(1)}`] = v;
                    }
                    const { data } = await api.get("epo-query/shipping", { params });
                    const list = Array.isArray(data?.Items) ? data.Items
                               : Array.isArray(data?.items) ? data.items
                               : Array.isArray(data?.results) ? data.results
                               : Array.isArray(data?.Results) ? data.Results : [];
                    return { rows: list, total: data?.Total ?? data?.total ?? 0 };
                },
            });
        } catch (e) {
            setExporting(false);
            return;
        }
        setExporting(false);
    };


    // R44 — server-side column filtering.
    const load = useCallback((pageNum, sizeNum, filters) => {
        let alive = true;
        setLoading(true);
        setError(null);
        const params = { page: pageNum ?? 1, pageSize: sizeNum ?? 25 };
        for (const [k, v] of Object.entries(filters || {})) {
            if (v == null || v === "") continue;
            params[`filter${k[0].toUpperCase()}${k.slice(1)}`] = v;
        }
        api.get("epo-query/shipping", { params })
            .then(({ data }) => {
                if (!alive) return;
                const items = data?.Items ?? data?.items ?? [];
                setRows(Array.isArray(items) ? items : []);
                const t  = data?.Total      ?? data?.total      ?? 0;
                const tp = data?.TotalPages ?? data?.totalPages ?? 1;
                const sp = data?.Page       ?? data?.page       ?? (pageNum ?? 1);
                setTotal(t);
                setTotalPages(Math.max(1, tp));
                setPage(sp);
            })
            .catch((e) => {
                if (!alive) return;
                const st = e?.response?.status;
                setError(
                    st === 403 || st === 401
                        ? "You are not authorized to view the shipping list."
                        : (e?.response?.data?.Message || e?.message || "Failed to load.")
                );
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    // R44 — initial + filter-change reload.
    useEffect(() => { setPage(1); if (allRows) return; return load(1, pageSize, columnFilters); }, [load, columnFilters, allRows]);  // eslint-disable-line

    const goToPage = (n) => {
        const t = Math.max(1, Math.min(effTotalPages, n));
        if (t === page) return;
        if (allRows) { setPage(t); return; } // Round 31 — local paging
        load(t, pageSize, columnFilters);  // R44
    };
    const changePageSize = (sz) => {
        const s2 = Number(sz) || 25;
        setPageSize(s2);
        if (allRows) { setPage(1); return; }
        load(1, s2, columnFilters);  // R44
    };

    // R41 — column definitions.
    // Batch 2 / Round 6 — derive a 3-state receiving status from the new
    // per-line progress fields the API now returns (LineCount /
    // LinesFullyReceived / AnyPartial), with a safe fallback to the binary
    // ItemsRecvd flag for any row that predates those fields.
    const statusOf = (r) => {
        const total = Number(pick(r, "LineCount", "lineCount") ?? 0);
        const full = Number(pick(r, "LinesFullyReceived", "linesFullyReceived") ?? 0);
        const anyPartial = !!pick(r, "AnyPartial", "anyPartial");
        const itemsRecvd = !!pick(r, "ItemsRecvd", "itemsRecvd");
        if (total > 0) {
            if (full >= total) return "Received";
            if (full > 0 || anyPartial) return "Partial";
            return "Pending";
        }
        return itemsRecvd ? "Received" : "Pending";
    };
    const statusColor = (s) => (s === "Received" ? "success" : s === "Partial" ? "warning" : "secondary");

    const columns = useMemo(() => [
        { id: "epoNumber",    label: "EPO #",       type: "text",
          get: r => pick(r, "EpoNumber", "epoNumber") },
        { id: "createdDate",  label: "Created",     type: "text",
          get: r => fmtDate(pick(r, "CreatedDate", "createdDate")) },
        { id: "createdBy",    label: "Created By",  type: "text",
          get: r => pick(r, "CreatedByName", "createdByName", "CreatedBy", "createdBy") },
        { id: "company",      label: "Company",     type: "text",
          get: r => pick(r, "Company", "company") },
        { id: "shipTo",       label: "Ship To",     type: "text",
          get: r => fmtAddress(r) },
        { id: "itemsRecvd",   label: "Received?",   type: "boolean",
          get: r => !!pick(r, "ItemsRecvd", "itemsRecvd") },
        { id: "status",       label: "Status",      type: "text",
          get: r => statusOf(r) },
        { id: "actions",      label: "",            type: "none" },
    ], []);

    // R44 — server returns already-filtered rows for this page.
    const visibleRowsBase = allRows
        ? applyColumnFilters(allRows, columns, columnFilters)
        : rows;
    // Round 30/31 — first sort click loads ALL rows; sort covers the dataset.
    const { sorted: visibleRows, sort, toggleSort } = useTableSort(visibleRowsBase, columns);
    const onSortToggle = (id) => {
        if (!allRows && !loadingAll) loadAllRows();
        toggleSort(id);
    };
    const effTotal = allRows ? visibleRows.length : total;
    const effTotalPages = allRows ? Math.max(1, Math.ceil(visibleRows.length / pageSize)) : totalPages;
    const pagedRows = allRows
        ? visibleRows.slice((page - 1) * pageSize, page * pageSize)
        : visibleRows;

    // Windowed page numbers.
    const w = 7;
    let lo = Math.max(1, page - 3);
    let hi = Math.min(effTotalPages, lo + w - 1);
    lo = Math.max(1, hi - w + 1);
    const nums = [];
    for (let i = lo; i <= hi; i++) nums.push(i);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title="Shipping"
                    breadcrumb={[{ label: "EPO", to: "/workflows?wf=epo" }, { label: "Shipping" }]}
                    subtitle={`EPOs awaiting receipt / shipment (${total} total)`}
                    breadcrumbItems={[{ label: "EPO", href: "/epo" }, { label: "Shipping" }]}
                />

                {error && <Alert color="danger" className="mt-2" toggle={() => setError(null)}>{error}</Alert>}

                <Card className="mt-3">
                    <CardBody>
                        {loading ? (
                            <div className="table-responsive epo-sticky-head">
                                    <table className="table table-striped table-bordered mb-0 align-middle">
                                        <thead className="table-light">
                                            <tr>{columns.map((c) => <th key={c.id}>{c.label}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                            <TableSkeleton rows={6} columns={columns.length} />
                                        </tbody>
                                    </table>
                                </div>
                        ) : rows.length === 0 ? (
                            <EmptyState
                                title="Nothing awaiting shipment"
                                description="No EPOs currently have unreceived lines."
                            />
                        ) : (
                            <>
                                                                {/* Round 29 — exports ALL rows matching the current filters (walks the same API, all pages) */}
                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-soft-primary btn-sm" disabled={!!exporting || !total} onClick={handleExportAll}>
                                        <i className="ri-download-2-line align-bottom me-1" />
                                        {exporting ? exporting : `Export all CSV (${total})`}
                                    </button>
                                                                    {loadingAll ? (
                                        <span className="badge bg-primary-subtle text-primary align-self-center ms-2">{loadingAll}</span>
                                    ) : allRows ? (
                                        <span className="badge bg-success-subtle text-success align-self-center ms-2" title="Sorting and filters now cover the full dataset">All {allRows.length} rows loaded</span>
                                    ) : null}
                                </div>
<div className="table-responsive epo-sticky-head">
                                    <table className="table table-striped table-bordered mb-0 align-middle">
                                        <thead className="table-light">
                                            <tr>
                                                {columns.map((c) => <SortableTh key={c.id} col={c} sort={sort} onToggle={onSortToggle} />)}
                                            </tr>
                                            <ColumnFilterRow
                                                columns={columns}
                                                filters={columnFilters}
                                                onChange={setColumnFilters}
                                            />
                                        </thead>
                                        <tbody>
                                            {pagedRows.map((r) => {
                                                const epoNum = pick(r, "EpoNumber", "epoNumber");
                                                return (
                                                    <tr key={epoNum} onDoubleClick={() => epoReview.open(epoNum)} style={{ cursor: "pointer" }}>
                                                        <td><strong>{epoNum}</strong></td>
                                                        <td>{fmtDate(pick(r, "CreatedDate", "createdDate"))}</td>
                                                        <td>{pick(r, "CreatedByName", "createdByName", "CreatedBy", "createdBy") ?? "—"}</td>
                                                        <td>{pick(r, "Company", "company") ?? "—"}</td>
                                                        <td style={{ maxWidth: 320 }}>{fmtAddress(r)}</td>
                                                        <td>
                                                            <Badge color={pick(r, "ItemsRecvd", "itemsRecvd") ? "success" : "secondary"}
                                                                pill style={{ fontSize: 11 }}>
                                                                {pick(r, "ItemsRecvd", "itemsRecvd") ? "Yes" : "No"}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            {(() => { const s = statusOf(r); return (
                                                                <Badge color={statusColor(s)} pill style={{ fontSize: 11 }}>{s}</Badge>
                                                            ); })()}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-link btn-sm p-0"
                                                                onClick={() => navigate(`/epo/${epoNum}/receive`)}
                                                            >Open</button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {visibleRows.length === 0 && (
                                    <div className="text-muted text-center py-3 small">
                                        No rows match the current filters.
                                    </div>
                                )}

                                {/* Pagination. */}
                                {total > 0 && (
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="text-muted small">Rows per page:</span>
                                            <Input type="select" bsSize="sm" value={pageSize}
                                                onChange={(e) => changePageSize(e.target.value)} style={{ width: 80 }}>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value={200}>200</option>
                                            </Input>
                                            <span className="text-muted small">
                                                {(allRows || visibleRows.length === rows.length)
                                                    ? <>Showing {effTotal === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, effTotal)} of {effTotal}{allRows ? " (all loaded)" : ""}</>
                                                    : <>Showing {visibleRows.length} of {rows.length} on this page (of {total} total)</>
                                                }
                                            </span>
                                        </div>
                                        <Pagination size="sm" className="mb-0">
                                            <PaginationItem disabled={page <= 1}><PaginationLink first onClick={() => goToPage(1)} /></PaginationItem>
                                            <PaginationItem disabled={page <= 1}><PaginationLink previous onClick={() => goToPage(page - 1)} /></PaginationItem>
                                            {nums.map((n) => (
                                                <PaginationItem key={n} active={n === page}>
                                                    <PaginationLink onClick={() => goToPage(n)}>{n}</PaginationLink>
                                                </PaginationItem>
                                            ))}
                                            <PaginationItem disabled={page >= effTotalPages}><PaginationLink next onClick={() => goToPage(page + 1)} /></PaginationItem>
                                            <PaginationItem disabled={page >= effTotalPages}><PaginationLink last onClick={() => goToPage(effTotalPages)} /></PaginationItem>
                                        </Pagination>
                                    </div>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        {epoReview.element}
        </div>
    );
};

export default EposShipping;
